import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useAdvancedWelcomeTourOptional } from "../../../context/AdvancedWelcomeTourContext";
import { fetchMenusByRestaurant, fetchRestoDataByUser } from "../../../services/restaurantService";
import {
  ADVANCED_TOUR_START_EVENT,
  dismissAdvancedWelcomeTourForSession,
  isAdvancedWelcomeTourComplete,
  markAdvancedWelcomeTourAutoPrompted,
  shouldOfferAdvancedWelcomeTour,
  wasAdvancedWelcomeTourAutoPrompted,
} from "../../../utils/advancedWelcomeTourStorage";
import "../../../assets/css/advanced-welcome-tour.css";

export default function AdvancedWelcomeTourLauncher() {
  const { t } = useTranslation();
  const location = useLocation();
  const tour = useAdvancedWelcomeTourOptional();
  const [showIntro, setShowIntro] = useState(false);

  const hideOnWizardPage = location.pathname.replace(/\/+$/, "").endsWith("/start-now");

  const evaluateAutoOffer = useCallback(async () => {
    if (!localStorage.getItem("tokenLogin")) return;
    if (hideOnWizardPage) return;
    if (tour?.isActive) return;
    if (isAdvancedWelcomeTourComplete()) return;
    if (wasAdvancedWelcomeTourAutoPrompted()) return;

    try {
      const [restoResult, menusResult] = await Promise.allSettled([
        fetchRestoDataByUser(),
        fetchMenusByRestaurant(),
      ]);

      const restaurant =
        restoResult.status === "fulfilled" ? restoResult.value?.restaurant : null;
      const menus =
        menusResult.status === "fulfilled" && Array.isArray(menusResult.value)
          ? menusResult.value
          : [];

      if (shouldOfferAdvancedWelcomeTour({ restaurant, menus })) {
        markAdvancedWelcomeTourAutoPrompted();
        setShowIntro(true);
      }
    } catch (error) {
      console.error("Advanced welcome tour check failed:", error);
    }
  }, [hideOnWizardPage, tour?.isActive]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void evaluateAutoOffer();
    }, 1200);

    return () => window.clearTimeout(timer);
  }, [evaluateAutoOffer, location.pathname]);

  useEffect(() => {
    const handleStart = () => {
      setShowIntro(false);
      void tour?.startTour();
    };

    window.addEventListener(ADVANCED_TOUR_START_EVENT, handleStart);
    return () => window.removeEventListener(ADVANCED_TOUR_START_EVENT, handleStart);
  }, [tour]);

  const handleStart = () => {
    setShowIntro(false);
    void tour?.startTour();
  };

  const handleLater = () => {
    dismissAdvancedWelcomeTourForSession();
    setShowIntro(false);
  };

  if (!showIntro || tour?.isActive) {
    return null;
  }

  return (
    <div className="advanced-welcome-tour-intro" role="dialog" aria-labelledby="advanced-welcome-intro-title">
      <div className="advanced-welcome-tour-intro__backdrop" onClick={handleLater} aria-hidden="true" />
      <div className="advanced-welcome-tour-intro__card">
        <span className="advanced-welcome-tour-intro__badge">{t("advTourIntroBadge")}</span>
        <div className="advanced-welcome-tour-intro__icon" aria-hidden="true">
          <i className="fas fa-route" />
        </div>
        <h2 id="advanced-welcome-intro-title">{t("advTourIntroTitle")}</h2>
        <p>{t("advTourIntroBody")}</p>
        <ul className="advanced-welcome-tour-intro__list">
          <li>{t("advTourIntroPoint1")}</li>
          <li>{t("advTourIntroPoint2")}</li>
          <li>{t("advTourIntroPoint3")}</li>
        </ul>
        <div className="advanced-welcome-tour-intro__actions">
          <button type="button" className="advanced-welcome-tour__btn advanced-welcome-tour__btn--ghost" onClick={handleLater}>
            {t("onboardingRemindLater")}
          </button>
          <button
            type="button"
            className="advanced-welcome-tour__btn advanced-welcome-tour__btn--primary"
            onClick={handleStart}
          >
            {t("advTourIntroStart")}
          </button>
        </div>
      </div>
    </div>
  );
}
